import classNames from "classnames";
import { Suspense } from "react";
import { useLocation, useRouteMatch } from "react-router-dom";
import {
  useFetchMeData,
  useHasTeammatePermissions,
} from "@common/features/User";
import { __ } from "@common/helpers/i18n";
import SubMenuItems from "@app/elements/container/SubMenuItems";
import { checkIfActive, withDashboardRootAlias } from "@app/helpers/menu";
import { MenuProps } from "./Menu";

export default function SubMenu({ generateMenu }: MenuProps) {
  const location = useLocation();
  const { url: baseUrl } = useRouteMatch();
  const pathSegments = location.pathname.split("/").filter(Boolean);

  const { data } = useFetchMeData();
  const canViewPosts = useHasTeammatePermissions("post_schedule");
  const accountSections = new Set([
    "post",
    "story",
    "posts",
    "draft",
    "schedule-calendar",
    "comment",
    "comments",
    "inbox",
    "automation",
    "automation-accounts",
  ]);

  if (
    data?.masterUser?.account_count === 0 &&
    accountSections.has(pathSegments[0])
  ) {
    return null;
  }

  const menu = generateMenu ? generateMenu(data!) : [];

  const active = menu.find((item) =>
    checkIfActive(
      location,
      withDashboardRootAlias([
        item.url,
        ...(item.childs || []).map((i) => (item.url ? baseUrl + i.url : i.url)),
      ]),
    ),
  );

  if (!active?.childs) {
    return null;
  }

  const Component = active.Component || SubMenuItems;
  const hiddenSettingsTabs = new Set(["/accounts", "/team", "/ticket"]);
  const isStandaloneSettingsPage =
    active.label === __("Settings") &&
    Array.from(hiddenSettingsTabs).some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`),
    );

  if (isStandaloneSettingsPage) {
    return null;
  }

  const items =
    active.label === __("Settings")
      ? active.childs.filter((item) => !hiddenSettingsTabs.has(item.url))
      : active.childs;
  const isSettingsTabs = active.label === __("Settings");
  const isAutomationEditorSubMenu =
    active.url === "/automation" &&
    ["response", "form", "product"].includes(pathSegments[2] || "") &&
    Boolean(pathSegments[3]) &&
    pathSegments[4] !== "responses";
  const showMobileSubMenu =
    Boolean(active.showSelectOnSmall) &&
    !isSettingsTabs &&
    !isAutomationEditorSubMenu;
  const isAutomationOverview =
    active.url === "/automation" && location.pathname === "/automation";

  if (items.length === 0 || isAutomationOverview || isAutomationEditorSubMenu) {
    return null;
  }

  const submenu = (
    <div
      data-admin-submenu-shell="true"
      className={classNames(
        "shrink-0",
        showMobileSubMenu ? "block" : "hidden md:block",
        isSettingsTabs && "mt-4",
      )}
    >
      <div className="mx-auto w-full max-w-[1440px]">
        <Suspense fallback={null}>
          <Component
            items={items}
            baseUrl={active.url ? baseUrl : ""}
            showSelectOnSmall={active.showSelectOnSmall}
          />
        </Suspense>
      </div>
    </div>
  );

  return ["posts", "draft", "schedule-calendar"].includes(pathSegments[0])
    ? canViewPosts
      ? submenu
      : null
    : submenu;
}
